// src/components/autopilot/ProspectCard.js
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Checkbox,
  Chip,
  Tooltip
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function ProspectCard({ prospect, selected = false, onToggle, showCheckbox = true }) {
  const matchScore = prospect.matchScore || 0;
  const isEnriched = prospect.enriched || false;
  const isVerified = prospect.verified || false;

  const getMatchColor = (score) => {
    if (score >= 80) return '#1BE885';
    if (score >= 60) return '#3b82f6';
    return '#f59e0b';
  };

  return (
    <Card
      sx={{
        mb: 2,
        boxShadow: 2,
        borderLeft: `4px solid ${selected ? '#1BE885' : '#e0e0e0'}`,
        cursor: showCheckbox ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
      onClick={showCheckbox && onToggle ? () => onToggle(prospect._id || prospect.id) : undefined}
    >
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Profile Image */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {prospect.profilePicture && !prospect.profilePicture.includes('linkedin-bug-color.png') ? (
              <Avatar
                src={prospect.profilePicture}
                alt={`${prospect.firstName} ${prospect.lastName}`}
                sx={{ width: 80, height: 80 }}
              />
            ) : (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 40, color: '#666' }} />
              </Box>
            )}
          </Box>

          {/* Profile Details */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {prospect.firstName} {prospect.lastName}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#666', mb: 1 }}>
                  {prospect.title}
                </Typography>
              </Box>
              {showCheckbox && (
                <Checkbox
                  checked={selected}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (onToggle) onToggle(prospect._id || prospect.id);
                  }}
                  sx={{ color: '#1BE885', '&.Mui-checked': { color: '#1BE885' } }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {prospect.company && (
                <Chip label={`Company: ${prospect.company}`} size="small" variant="outlined" />
              )}
              {prospect.location && (
                <Chip label={`Location: ${prospect.location}`} size="small" variant="outlined" />
              )}
              {prospect.experience && (
                <Chip label={`Experience: ${prospect.experience}`} size="small" variant="outlined" />
              )}
            </Box>

            {prospect.summary && (
              <Typography variant="body2" sx={{ color: '#888', mb: 1 }}>
                {prospect.summary}
              </Typography>
            )}

            {/* Status Indicators */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <Tooltip title={`Match Score: ${matchScore}%`}>
                <Chip
                  label={`${matchScore}% Match`}
                  size="small"
                  sx={{
                    bgcolor: getMatchColor(matchScore),
                    color: '#fff',
                    fontWeight: 600
                  }}
                />
              </Tooltip>
              {isEnriched && (
                <Tooltip title="Enriched via PDL">
                  <Chip
                    icon={<VerifiedIcon />}
                    label="Enriched"
                    size="small"
                    sx={{ bgcolor: '#3b82f6', color: '#fff' }}
                  />
                </Tooltip>
              )}
              {isVerified && (
                <Tooltip title="Verified against search criteria">
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Verified"
                    size="small"
                    sx={{ bgcolor: '#1BE885', color: '#fff' }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default ProspectCard;
